import { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Badge,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Attachment as AttachmentIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";

const AttachmentsSection = ({ attachments, convertUTCToLocal, t }) => {
  const documents = attachments.filter((file) => !file.is_image);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [downloadedFileName, setDownloadedFileName] = useState("");

  const handleDownload = (file) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.display_name || "download";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success notification
    setDownloadedFileName(file.display_name || "File");
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  if (documents.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        {t("observationDetails.noDocumentsAttached", "No documents attached")}
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 3,
        }}
      >
        <AttachmentIcon
          sx={{
            color: "warning.main",
            fontSize: { xs: 18, sm: 20 },
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.125rem" },
          }}
        >
          {t("observationDetails.attachmentsTitle")}
        </Typography>
        <Chip
          label={attachments.length}
          size="small"
          color="warning"
          variant="outlined"
          sx={{
            fontWeight: 600,
            minWidth: 32,
          }}
        />
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <AttachmentIcon
            sx={{
              color: "secondary.main",
              fontSize: { xs: 16, sm: 18 },
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            {t("observationDetails.documentsTitle", "Documents")}
          </Typography>
          <Badge
            badgeContent={documents.length}
            color="secondary"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.75rem",
                fontWeight: 600,
                minWidth: 20,
                height: 20,
              },
            }}
          >
            <Box />
          </Badge>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
          }}
        >
          {documents.map((file) => (
            <Box
              key={file.id}
              onClick={() => handleDownload(file)}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              <Box
                sx={{
                  height: { xs: 100, sm: 120 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "action.hover",
                }}
              >
                <AttachmentIcon
                  sx={{
                    fontSize: 32,
                    color: "text.secondary",
                  }}
                />
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    mb: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.display_name || t("observationDetails.fileLabel")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.65rem",
                  }}
                >
                  {convertUTCToLocal(file.created_at)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Download Success Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t(
              "observationDetails.downloadStarted",
              "Download started: {{fileName}}",
              { fileName: downloadedFileName }
            )}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {t(
              "observationDetails.checkDownloadFolder",
              "Please check your downloads folder"
            )}
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

AttachmentsSection.propTypes = {
  attachments: PropTypes.array.isRequired,
  convertUTCToLocal: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default AttachmentsSection;
